import { useState } from 'react'
import { Box, HStack, Text, VStack, Badge } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap'
import Avatar from 'components/Avatar'
import { AuthRoleIdEnum } from 'constants/user'
import { MembershipInfo } from 'types/auth'
import { getRoleNameFromId } from 'utils/auth/role'
import styles from './styles.module.scss'

/** Dropdown item for a single org in the switcher */
const OrgItem = ({
  membership,
  isActive,
  onSelect
}: {
  membership: MembershipInfo
  isActive: boolean
  onSelect: () => void
}) => (
  <Box
    as="button"
    w="100%"
    px={3}
    py={2}
    bg={isActive ? 'blue.50' : 'transparent'}
    _hover={{ bg: isActive ? 'blue.50' : 'gray.50' }}
    border="none"
    cursor="pointer"
    textAlign="left"
    onClick={onSelect}
    borderRadius="6px"
  >
    <HStack spacing={3}>
      <Avatar src={membership.orgLogo ?? ''} name={membership.orgName} size={32} />
      <VStack align="flex-start" spacing={0} flex={1}>
        <Text fontSize="sm" fontWeight={isActive ? '600' : '400'} color="gray.800" m={0} noOfLines={1}>
          {membership.orgName}
        </Text>
        <Badge
          colorScheme={
            membership.roleId === AuthRoleIdEnum.OWNER
              ? 'orange'
              : membership.roleId === AuthRoleIdEnum.ADMIN
                ? 'purple'
                : membership.roleId === AuthRoleIdEnum.COACH
                  ? 'blue'
                  : 'gray'
          }
          fontSize="10px"
          borderRadius="3px"
        >
          {getRoleNameFromId(membership.roleId)}
        </Badge>
      </VStack>
    </HStack>
  </Box>
)

/** Header dropdown for switching between orgs without re-login */
const OrgSwitcher = () => {
  const { authStore } = useStores()
  const { memberships, activeOrgId, currentMembership } = authStore
  const [isOpen, setIsOpen] = useState(false)

  // Only show switcher if user has multiple memberships
  if (memberships.length <= 1) return null

  const handleOrgSwitch = (orgId: number) => {
    if (orgId === activeOrgId) {
      setIsOpen(false)
      return
    }
    authStore.setActiveOrg(orgId)
    setIsOpen(false)
    // Reload to refetch every team-scoped query with the new active team.
    window.location.reload()
  }

  return (
    <Dropdown className={styles.orgSwitcher} isOpen={isOpen} toggle={() => setIsOpen(!isOpen)}>
      <DropdownToggle
        tag="button"
        className={styles.toggleButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Switch team"
      >
        <HStack spacing={2}>
          <Avatar src={currentMembership?.orgLogo ?? ''} name={currentMembership?.orgName ?? ''} size={24} />
          <Text fontSize="sm" fontWeight="500" color="gray.700" m={0} noOfLines={1} maxW="120px">
            {currentMembership?.orgName ?? 'Select organization'}
          </Text>
          <i className="las la-angle-down" style={{ fontSize: '12px', color: '#718096' }} />
        </HStack>
      </DropdownToggle>
      <DropdownMenu className={styles.dropdownMenu}>
        <VStack spacing={1} p={2} align="stretch">
          <Text fontSize="xs" fontWeight="600" color="gray.500" px={3} py={1} m={0} textTransform="uppercase">
            Teams
          </Text>
          {memberships.map(m => (
            <OrgItem
              key={m.orgId}
              membership={m}
              isActive={m.orgId === activeOrgId}
              onSelect={() => handleOrgSwitch(m.orgId)}
            />
          ))}
        </VStack>
      </DropdownMenu>
    </Dropdown>
  )
}

export default observer(OrgSwitcher)
